export function normalizeTipoParam(str) {
  return String(str ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[áàä]/g, "a")
    .replace(/[éèë]/g, "e")
    .replace(/[íìï]/g, "i")
    .replace(/[óòö]/g, "o")
    .replace(/[úùü]/g, "u");
}

const WIDE_METRIC_KEYS = new Set([
  "espesor_deposito",
  "volumen_material",
  "area_afectada",
  "distancia_alcanzada",
]);
const WIDE_METRIC_WIDTH_PCT = 11;
const VELOCIDAD_MIN_WIDTH_PCT = 8;

export function normalizePctWidths(cols, targetTotal = 100) {
  const next = cols.map((c) => ({ ...c }));
  const pctCols = next.filter(
    (c) => c && typeof c.widthPct === "number" && !Number.isNaN(c.widthPct),
  );
  if (!pctCols.length) return next;

  const sum = pctCols.reduce((acc, c) => acc + c.widthPct, 0);
  if (sum <= 0) return next;

  if (sum > targetTotal + 0.0001) {
    const ratio = targetTotal / sum;
    const scaled = pctCols.map((c) => ({
      c,
      raw: c.widthPct * ratio,
      floored: 0,
      frac: 0,
    }));
    scaled.forEach((s) => {
      s.floored = Math.floor(s.raw * 100) / 100;
      s.frac = s.raw - s.floored;
    });
    let rem =
      Math.round(
        (targetTotal - scaled.reduce((a, s) => a + s.floored, 0)) * 100,
      ) / 100;
    scaled.sort((a, b) => b.frac - a.frac);
    let i = 0;
    while (rem > 0.0001 && scaled.length) {
      scaled[i % scaled.length].floored =
        Math.round((scaled[i % scaled.length].floored + 0.01) * 100) / 100;
      rem = Math.round((rem - 0.01) * 100) / 100;
      i += 1;
      if (i > 5000) break;
    }
    scaled.forEach((s) => {
      s.c.widthPct = s.floored;
    });
    return next;
  }

  if (sum < targetTotal - 0.0001) {
    const deficit = Math.round((targetTotal - sum) * 100) / 100;
    const report = pctCols.find((c) => c.key === "__reporte");
    const fuente = pctCols.find((c) => c.key === "__fuente");
    if (report) {
      report.widthPct = Math.round((report.widthPct + deficit) * 100) / 100;
      return next;
    }
    if (fuente) {
      fuente.widthPct = Math.round((fuente.widthPct + deficit) * 100) / 100;
      return next;
    }
    const shareEach = Math.floor((deficit / pctCols.length) * 100) / 100;
    pctCols.forEach((c) => {
      c.widthPct = Math.round((c.widthPct + shareEach) * 100) / 100;
    });
    const newSum = pctCols.reduce((a, c) => a + c.widthPct, 0);
    const rem2 = Math.round((targetTotal - newSum) * 100) / 100;
    if (Math.abs(rem2) > 0.0001) {
      pctCols[0].widthPct =
        Math.round((pctCols[0].widthPct + rem2) * 100) / 100;
    }
  }
  return next;
}

function widenTopColumns(cols) {
  const next = cols.map((c) => ({ ...c }));
  const dataCols = next.filter(
    (c) => c.key !== "__index" && c.key !== "__imagenes",
  );
  for (let i = 0; i < dataCols.length; i += 1) {
    const c = dataCols[i];
    if (!c) continue;

    if (c.key === "__fuente") {
      c.widthPct = 14;
      continue;
    }
    if (c.key === "__reporte") {
      c.widthPct = 28;
      continue;
    }

    const pos = i + 1;
    if (pos >= 3 && pos <= 7) {
      const floor =
        c.key === "velocidad" ? VELOCIDAD_MIN_WIDTH_PCT : WIDE_METRIC_WIDTH_PCT;
      c.widthPct = Math.max(Number(c.widthPct ?? 0) || 0, floor);
    }

    if (WIDE_METRIC_KEYS.has(c.key)) {
      c.widthPct = Math.max(
        Number(c.widthPct ?? 0) || 0,
        WIDE_METRIC_WIDTH_PCT,
      );
    }
  }
  return normalizePctWidths(next);
}

export function getParamColumns(tipoNorm) {
  const common = [
    { key: "__index", label: "#", width: 22, align: "center" },
    { key: "__fuente", label: "Fuente", widthPct: 11, align: "center" },
    {
      key: "__reporte",
      label: "Reporte bibliográfico",
      widthPct: 28,
      align: "justify",
    },
  ];

  const imgCol = {
    key: "__imagenes",
    label: "",
    width: 32,
    align: "center",
    isImages: true,
  };

  if (tipoNorm === "lahar") {
    const cols = [
      ...common,
      {
        key: "subtipo",
        label: "Subtipo",
        align: "justify",
        widthPct: 8,
      },
      {
        key: "espesor_deposito",
        label: "Espesor del depósito (m)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "volumen_material",
        label: "Volumen del material (m³)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "area_afectada",
        label: "Área afectada (km²)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "velocidad",
        label: "Velocidad (km/h)",
        align: "center",
        widthPct: 7,
      },
      imgCol,
    ];
    return widenTopColumns(cols);
  }

  if (tipoNorm === "caida piroclastica") {
    const cols = [
      ...common,
      { key: "subtipo", label: "Subtipo", align: "center", widthPct: 8 },
      {
        key: "altura_columna",
        label: "Altura de la columna (km)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "masa_total_eruptada",
        label: "Masa total eruptada (kg)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "duracion_evento",
        label: "Duración del evento (min)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "velocidad",
        label: "Velocidad (km/h)",
        align: "center",
        widthPct: 7,
      },
      imgCol,
    ];
    return widenTopColumns(cols);
  }

  if (tipoNorm === "corriente de densidad piroclastica") {
    const cols = [
      ...common,
      {
        key: "subtipo",
        label: "Subtipo",
        align: "center",
        widthPct: 8,
      },
      {
        key: "caracterizacion_tamanio",
        label: "Caracterización de tamaño",
        align: "justify",
        widthPct: 9,
      },
      {
        key: "espesor_deposito",
        label: "Espesor del depósito (m)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "area_afectada",
        label: "Área afectada (km²)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "distancia_alcanzada",
        label: "Distancia alcanzada (km)",
        align: "center",
        widthPct: 7,
      },
      imgCol,
    ];
    return widenTopColumns(cols);
  }

  if (tipoNorm === "domo de lava") {
    const cols = [
      ...common,
      { key: "altura", label: "Altura (m)", align: "center", widthPct: 7 },
      {
        key: "area_superficial",
        label: "Área superficial (m²)",
        align: "center",
        widthPct: 7,
      },
      { key: "volumen", label: "Volumen (m³)", align: "center", widthPct: 7 },
      {
        key: "tasa_extrusion",
        label: "Tasa de extrusión (m³/s)",
        align: "center",
        widthPct: 8,
      },
      {
        key: "tipo_actividad",
        label: "Tipo de actividad",
        align: "center",
        widthPct: 7,
      },
      imgCol,
    ];
    return widenTopColumns(cols);
  }

  if (tipoNorm === "flujo de lava") {
    const cols = [
      ...common,
      { key: "subtipo", label: "Subtipo", align: "center", widthPct: 8 },
      {
        key: "longitud_flujo",
        label: "Longitud del flujo (m)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "area_cubierta",
        label: "Área cubierta (m²)",
        align: "center",
        widthPct: 7,
      },
      { key: "volumen", label: "Volumen (m³)", align: "center", widthPct: 7 },
      {
        key: "tasa_efusion",
        label: "Tasa de efusión (m³/s)",
        align: "center",
        widthPct: 8,
      },
      imgCol,
    ];
    return widenTopColumns(cols);
  }

  if (tipoNorm === "avalancha de escombros") {
    const cols = [
      ...common,
      {
        key: "espesor_deposito",
        label: "Espesor del depósito (m)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "volumen_material",
        label: "Volumen del material (m³)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "area_afectada",
        label: "Área afectada (km²)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "distancia_alcanzada",
        label: "Distancia alcanzada (km)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "velocidad",
        label: "Velocidad (km/h)",
        align: "center",
        widthPct: 7,
      },
      imgCol,
    ];
    return widenTopColumns(cols);
  }

  if (tipoNorm === "gases y aerosoles" || tipoNorm === "gases aerosoles") {
    const cols = [
      ...common,
      {
        key: "valores_emision_gas",
        label: "Valores de emisión del gas",
        align: "center",
        widthPct: 10,
      },
      {
        key: "distancia_alcanzada",
        label: "Distancia alcanzada (km)",
        align: "center",
        widthPct: 9,
      },
      {
        key: "tipo_actividad",
        label: "Tipo de actividad",
        align: "center",
        widthPct: 7,
      },
      {
        key: "estilo_eruptivo",
        label: "Estilo eruptivo",
        align: "center",
        widthPct: 7,
      },
      imgCol,
    ];
    return widenTopColumns(cols);
  }

  if (tipoNorm === "sismos volcanicos") {
    const cols = [
      ...common,
      {
        key: "tipo_seniales_sismicas",
        label: "Tipo de señales sísmicas",
        align: "center",
        widthPct: 9,
      },
      {
        key: "magnitud",
        label: "Magnitud (ML, Md, Me)",
        align: "center",
        widthPct: 7,
      },
      {
        key: "municipios_afectados",
        label: "Sitios afectados",
        align: "center",
        widthPct: 9,
      },
      {
        key: "area_percepcion",
        label: "Área de percepción",
        align: "center",
        widthPct: 7,
      },
      {
        key: "tipo_actividad",
        label: "Tipo de actividad",
        align: "center",
        widthPct: 7,
      },
      imgCol,
    ];
    return widenTopColumns(cols);
  }

  return widenTopColumns([...common, imgCol]);
}

export function getParamExcelColumnDefs(tipoNorm) {
  const F = { key: "__fuente", label: "Fuente" };
  const R = { key: "__reporte", label: "Reporte bibliográfico" };

  if (tipoNorm === "lahar") {
    return [
      F,
      R,
      { key: "subtipo", label: "Subtipo" },
      { key: "altura_flujo", label: "Altura del flujo (m)" },
      { key: "espesor_deposito", label: "Espesor del depósito (m)" },
      { key: "volumen_material", label: "Volumen del material (m³)" },
      { key: "area_afectada", label: "Área afectada (km²)" },
      { key: "distancia_alcanzada", label: "Distancia alcanzada (km)" },
      { key: "duracion_evento", label: "Duración del evento (minutos)" },
      { key: "caudal", label: "Caudal (m³/s)" },
      { key: "velocidad", label: "Velocidad (km/h)" },
      { key: "dataciones", label: "Dataciones" },
      { key: "caracterizacion_tamanio", label: "Caracterización de tamaño" },
      { key: "tipo_actividad", label: "Tipo de actividad" },
      { key: "estilo_eruptivo", label: "Estilo eruptivo" },
    ];
  }

  if (tipoNorm === "caida piroclastica") {
    return [
      F,
      R,
      { key: "subtipo", label: "Subtipo" },
      { key: "altura_columna", label: "Altura de la columna (km)" },
      { key: "espesor_deposito", label: "Espesor del depósito (m)" },
      { key: "masa_total_eruptada", label: "Masa total eruptada (kg)" },
      { key: "volumen_material", label: "Volumen del material (m³)" },
      { key: "area_afectada", label: "Área afectada (km²)" },
      { key: "distancia_alcanzada", label: "Distancia alcanzada (km)" },
      { key: "duracion_evento", label: "Duración del evento (minutos)" },
      {
        key: "tasa_emision_material",
        label: "Tasa de emisión de material (kg/s)",
      },
      { key: "velocidad", label: "Velocidad (km/h)" },
      { key: "dataciones", label: "Dataciones" },
      { key: "caracterizacion_tamanio", label: "Caracterización de tamaño" },
      { key: "petrografia", label: "Petrografía" },
      { key: "geoquimica", label: "Geoquímica" },
      { key: "quimica_mineral", label: "Química mineral" },
      { key: "geotermobarometria", label: "Geotermobarometría" },
      { key: "tipo_actividad", label: "Tipo de actividad" },
      { key: "estilo_eruptivo", label: "Estilo eruptivo" },
    ];
  }

  if (tipoNorm === "corriente de densidad piroclastica") {
    return [
      F,
      R,
      { key: "subtipo", label: "Subtipo" },
      { key: "espesor_deposito", label: "Espesor del depósito (m)" },
      { key: "volumen_material", label: "Volumen del material (m³)" },
      { key: "area_afectada", label: "Área afectada (km²)" },
      { key: "distancia_alcanzada", label: "Distancia alcanzada (km)" },
      { key: "duracion_evento", label: "Duración del evento (minutos)" },
      { key: "dataciones", label: "Dataciones" },
      { key: "caracterizacion_tamanio", label: "Caracterización de tamaño" },
      {
        key: "temperatura_emplazamiento",
        label: "Temperatura de emplazamiento (°C)",
      },
      { key: "petrografia", label: "Petrografía" },
      { key: "geoquimica", label: "Geoquímica" },
      { key: "quimica_mineral", label: "Química mineral" },
      { key: "geotermobarometria", label: "Geotermobarometría" },
      { key: "tipo_actividad", label: "Tipo de actividad" },
      { key: "estilo_eruptivo", label: "Estilo eruptivo" },
    ];
  }

  if (tipoNorm === "domo de lava") {
    return [
      F,
      R,
      { key: "altura", label: "Altura (m)" },
      { key: "perimetro", label: "Perímetro (m)" },
      { key: "area_superficial", label: "Área superficial (m²)" },
      { key: "volumen", label: "Volumen (m³)" },
      { key: "tasa_extrusion", label: "Tasa de extrusión (m³/s)" },
      { key: "petrografia", label: "Petrografía" },
      { key: "geoquimica", label: "Geoquímica" },
      { key: "quimica_mineral", label: "Química mineral" },
      { key: "geotermobarometria", label: "Geotermobarometría" },
      { key: "tipo_actividad", label: "Tipo de actividad" },
      { key: "estilo_eruptivo", label: "Estilo eruptivo" },
    ];
  }

  if (tipoNorm === "flujo de lava") {
    return [
      F,
      R,
      { key: "subtipo", label: "Subtipo" },
      { key: "espesor", label: "Espesor (m)" },
      { key: "longitud_flujo", label: "Longitud del flujo (m)" },
      { key: "area_cubierta", label: "Área cubierta (m²)" },
      { key: "volumen", label: "Volumen (m³)" },
      { key: "velocidad_avance", label: "Velocidad de avance (km/h)" },
      { key: "tasa_efusion", label: "Tasa de efusión (m³/s)" },
      { key: "petrografia", label: "Petrografía" },
      { key: "geoquimica", label: "Geoquímica" },
      { key: "quimica_mineral", label: "Química mineral" },
      { key: "geotermobarometria", label: "Geotermobarometría" },
      { key: "tipo_actividad", label: "Tipo de actividad" },
      { key: "estilo_eruptivo", label: "Estilo eruptivo" },
    ];
  }

  if (tipoNorm === "avalancha de escombros") {
    return [
      F,
      R,
      { key: "espesor_deposito", label: "Espesor del depósito (m)" },
      { key: "volumen_material", label: "Volumen del material (m³)" },
      { key: "area_afectada", label: "Área afectada (km²)" },
      { key: "distancia_alcanzada", label: "Distancia alcanzada (km)" },
      { key: "duracion_evento", label: "Duración del evento (minutos)" },
      { key: "velocidad", label: "Velocidad (km/h)" },
      { key: "dataciones", label: "Dataciones" },
      { key: "caracterizacion_tamanio", label: "Caracterización de tamaño" },
      { key: "tipo_actividad", label: "Tipo de actividad" },
      { key: "estilo_eruptivo", label: "Estilo eruptivo" },
    ];
  }

  if (tipoNorm === "gases y aerosoles" || tipoNorm === "gases aerosoles") {
    return [
      F,
      R,
      { key: "valores_emision_gas", label: "Valores de emisión del gas" },
      { key: "distancia_alcanzada", label: "Distancia alcanzada (km)" },
      { key: "tipo_actividad", label: "Tipo de actividad" },
      { key: "estilo_eruptivo", label: "Estilo eruptivo" },
    ];
  }

  if (tipoNorm === "sismos volcanicos") {
    return [
      F,
      R,
      { key: "tipo_seniales_sismicas", label: "Tipo de señales sísmicas" },
      { key: "latitud", label: "Latitud" },
      { key: "longitud", label: "Longitud" },
      { key: "magnitud", label: "Magnitud (ML, Md, Me)" },
      { key: "profundidad", label: "Profundidad (km)" },
      { key: "municipios_afectados", label: "Municipios afectados" },
      { key: "area_percepcion", label: "Área de percepción" },
      { key: "tipo_actividad", label: "Tipo de actividad" },
    ];
  }

  return getParamColumns(tipoNorm)
    .filter((c) => c.key !== "__index" && c.key !== "__imagenes")
    .map((c) => ({
      key: c.key,
      label: c.key === "__fuente" ? "Fuente" : c.label,
    }));
}

export const MAX_PARAM_VISIBLE_COLUMNS = 8;
export function getParamPickerDefs(tipoNorm) {
  const idx = { key: "__index", label: "#" };
  const body = getParamExcelColumnDefs(tipoNorm);
  const tableCols = getParamColumns(tipoNorm);
  const hasImages = tableCols.some((c) => c.key === "__imagenes");
  const list = [idx, ...body];
  if (hasImages) list.push({ key: "__imagenes", label: "Imágenes" });
  const seen = new Set();
  const out = [];
  for (const item of list) {
    if (!item?.key || seen.has(item.key)) continue;
    seen.add(item.key);
    out.push({
      key: item.key,
      label: String(item.label ?? "").trim() || item.key,
    });
  }
  return out;
}

export function getDefaultVisibleKeysForParamPicker(tipoNorm) {
  const full = getParamColumns(tipoNorm).map((c) => c.key);
  const hasImg = full.includes("__imagenes");
  const withoutIdx = full.filter((k) => k !== "__index");

  if (!hasImg) return withoutIdx.slice(0, MAX_PARAM_VISIBLE_COLUMNS);

  const withoutIdxOrImg = withoutIdx.filter((k) => k !== "__imagenes");
  return [...withoutIdxOrImg, "__imagenes"].slice(0, MAX_PARAM_VISIBLE_COLUMNS);
}

export function mergePickerOrder(canonicalKeys, savedKeys) {
  const canon = Array.isArray(canonicalKeys) ? canonicalKeys : [];
  const s = Array.isArray(savedKeys) ? savedKeys : [];
  const seen = new Set();
  const out = [];
  for (const k of s) {
    if (canon.includes(k) && !seen.has(k)) {
      out.push(k);
      seen.add(k);
    }
  }
  for (const k of canon) {
    if (!seen.has(k)) {
      out.push(k);
      seen.add(k);
    }
  }
  return out;
}

export function clampVisibleMap(
  order,
  visible,
  max = MAX_PARAM_VISIBLE_COLUMNS,
) {
  const visKeys = order.filter((k) => visible[k]);
  if (visKeys.length <= max) return { ...visible };
  let toRemove = visKeys.length - max;
  const next = { ...visible };
  for (let i = order.length - 1; i >= 0 && toRemove > 0; i -= 1) {
    const k = order[i];
    if (next[k]) {
      next[k] = false;
      toRemove -= 1;
    }
  }
  return next;
}

export function ensureAtLeastOneVisible(order, visible) {
  const n = order.filter((k) => visible[k]).length;
  if (n > 0) return { ...visible };
  const next = { ...visible };
  const first = order[0];
  if (first) next[first] = true;
  return next;
}

export function paramPickerStorageKey(tipoNorm) {
  return `evt.paramCols.v1:${tipoNorm || "_"}`;
}

export function loadParamPickerState(tipoNorm) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(paramPickerStorageKey(tipoNorm));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveParamPickerState(tipoNorm, state) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      paramPickerStorageKey(tipoNorm),
      JSON.stringify(state),
    );
  } catch {
    // ignore
  }
}

export function buildInitialPickerState(tipoNorm) {
  const defs = getParamPickerDefs(tipoNorm);
  const canonical = defs.map((d) => d.key);
  const saved = loadParamPickerState(tipoNorm);
  const order = mergePickerOrder(canonical, saved?.order);
  const defaultVis = new Set(getDefaultVisibleKeysForParamPicker(tipoNorm));
  const vis = {};
  for (const k of order) {
    const hasSaved =
      saved &&
      saved.visible &&
      Object.prototype.hasOwnProperty.call(saved.visible, k);
    vis[k] = hasSaved ? Boolean(saved.visible[k]) : defaultVis.has(k);
  }
  let clamped = clampVisibleMap(order, vis);
  clamped = ensureAtLeastOneVisible(order, clamped);
  return { order, visible: clamped };
}

export function buildResetPickerState(tipoNorm) {
  const defs = getParamPickerDefs(tipoNorm);
  const order = defs.map((d) => d.key);
  const dv = new Set(getDefaultVisibleKeysForParamPicker(tipoNorm));
  const vis = Object.fromEntries(order.map((k) => [k, dv.has(k)]));
  let clamped = clampVisibleMap(order, vis);
  clamped = ensureAtLeastOneVisible(order, clamped);
  return { order, visible: clamped };
}

export function buildTableColumnsFromPicker(tipoNorm, order, visible) {
  const tableTemplate = getParamColumns(tipoNorm);
  const tmplByKey = new Map(tableTemplate.map((c) => [c.key, c]));
  const labelByKey = new Map(
    getParamPickerDefs(tipoNorm).map((d) => [d.key, d.label]),
  );
  const keys = order.filter((k) => visible[k]);
  const cols = keys.map((key) => {
    const t = tmplByKey.get(key);
    if (t) return { ...t };
    if (key === "__index") {
      return {
        key: "__index",
        label: "#",
        width: 22,
        align: "center",
      };
    }
    if (key === "__fuente") {
      return {
        key: "__fuente",
        label: "Fuente",
        widthPct: 11,
        align: "center",
      };
    }
    if (key === "__reporte") {
      return {
        key: "__reporte",
        label: labelByKey.get("__reporte") || "Reporte bibliográfico",
        widthPct: 28,
        align: "justify",
      };
    }
    if (key === "__imagenes") {
      return {
        key: "__imagenes",
        label: "",
        width: 32,
        align: "center",
        isImages: true,
      };
    }
    return {
      key,
      label: labelByKey.get(key) || key,
      align: "center",
      widthPct: 11,
    };
  });
  return widenTopColumns(cols);
}
